import { useContext } from 'react';

import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';

import type { AnalyticsOrigin } from '../../constants/snippetAnalytics.ts';
import { ANALYTICS_UI_SUBJECT_ID } from '../../constants/snippetAnalytics.ts';
import { DeleteSnippetModal } from '../../modals/DeleteSnippetModal.tsx';
import { DuplicateSnippetModal } from '../../modals/DuplicateSnippetModal.tsx';
import { EditSnippetModal } from '../../modals/EditSnippetModal.tsx';
import type { Snippet } from '../../types/snippet.ts';

import { useSnippetAnalytics } from '../analytics/useSnippetAnalytics.ts';

export type SnippetActionHandlersOptions = {
	analyticsOrigin: AnalyticsOrigin;
	contentId?: string;
	spaceId?: string;
	spaceKey?: string;
};

export const useSnippetActionHandlers = ({
	analyticsOrigin,
	contentId = '',
	spaceId = '',
	spaceKey = '',
}: SnippetActionHandlersOptions) => {
	const { fireButtonClickedAnalytic } = useSnippetAnalytics(analyticsOrigin);
	const { showModal } = useDialogs();
	const { editorAPI } = useContext(EditorContextPanelContext);

	const handleEdit = (snippet: Snippet) => {
		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.EDIT_SNIPPET, {
			snippetId: snippet.snippetId,
			scope: snippet.scope,
		});
		showModal(EditSnippetModal, {
			snippet,
			editorAPI,
			contentId,
			spaceId,
			spaceKey,
		});
	};

	const handleDuplicate = (snippet: Snippet) => {
		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.DUPLICATE_SNIPPET, {
			snippetId: snippet.snippetId,
			scope: snippet.scope,
		});
		showModal(DuplicateSnippetModal, {
			snippet,
			editorAPI,
			contentId,
			spaceId,
			spaceKey,
			analyticsOrigin,
		});
	};

	const handleDelete = (snippet: Snippet) => {
		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.DELETE_SNIPPET, {
			snippetId: snippet.snippetId,
			scope: snippet.scope,
		});
		showModal(DeleteSnippetModal, { snippet, editorAPI });
	};

	return { handleEdit, handleDuplicate, handleDelete };
};
